import * as THREE from 'three';
import { ft } from '../core/units.js';
import { byId as departmentsById } from '../data/departments.js';
import feel from '../core/feel.json';
import { createRig } from './rig.js';
import { appearanceFor } from './appearance.js';

/**
 * A person in their department's colour, and — for the ones with eyes — a cone
 * on the floor showing exactly what they can see.
 *
 * The same rig the player uses, from the same file, at the same size. That is
 * not tidiness: everybody in this building is collided as the same capsule and
 * seen by the same cones, so a roster drawn differently from the player would
 * be telling the player something untrue about who they are looking at. Twenty
 * of them on screen at once, all of them walking their own routes at their own
 * speeds, and the stride of each is driven by the ground that one is covering.
 *
 * The cone is drawn because the write-up system is unplayable without it. A
 * stealth rule the player cannot see the shape of is a rule they learn by being
 * punished, and the whole point of a fixed patrol is that it is learnable. It
 * is a flat fan on the floor rather than a volume, because on the floor it
 * survives being looked at from any angle.
 *
 * Names are DOM, projected to screen space each frame, for the same reason the
 * HUD is: text in the scene means a texture, and a texture is an asset.
 */
/** Feet a standing person may be from a seat and still be drawn sat on it. */
const SIT_REACH = 6;
/** Seconds of standing still before somebody sits. */
const SIT_AFTER = 1.5;
/** Feet within which somebody turns toward the player when they speak. */
const TURN_REACH = 16;
/** The most a person turns from where they are facing, toward you. */
const TURN_MOST = (65 * Math.PI) / 180;

const wrapAngle = (a) => Math.atan2(Math.sin(a), Math.cos(a));

/**
 * `seats` are places a person standing still may be drawn sitting: points in
 * metres, each with the yaw a body sat there faces. `player` is where the
 * player is, read, for a person who speaks to turn toward.
 *
 * Both are drawing and nothing else. The person's position, yaw and sight
 * cone are untouched: perception, routes and every alibi read those, and they
 * stay where the simulation put them. Only the body is drawn turned a little
 * toward you, or sat on the bench beside where they are standing.
 */
/** What somebody on a break has in their hand, as the body is told it. */
const BREAK_HELD = Object.freeze({
  phone: { kind: 'remote', shape: 'box', width: 0.075, height: 0.15, colour: 0x15181d },
  food: { kind: 'breadroll', shape: 'sphere', width: 0.1, height: 0.07, colour: 0xc8955a },
  coffee: { kind: 'coffeecup', shape: 'cylinder', width: 0.085, height: 0.1, colour: 0xf2efe8 },
});

/*
 * `life` says what somebody is doing that only shows: on a break with a phone,
 * food or a coffee (`does`), and who they are talking to (`facing`, a point to
 * turn the drawn body toward). Drawing only, like everything else here.
 */
export function createNpcView({ npc, parent, talkingTo = () => null, seats = [], player = () => null, life = () => null, named = () => true }) {
  /**
   * A guest has no department, because a guest does not work here. Drawn in
   * the house colour and labelled by what they are, which is the only place in
   * the game that has to know the two kinds of person apart at all — and it is
   * a label, not a rule.
   */
  const department = departmentsById[npc.data.department] ?? {
    name: npc.data.affiliation ?? 'Guest',
    colour: 0x9c8f7a,
  };
  const group = new THREE.Group();
  group.name = `npc-${npc.data.id}`;

  const radius = feel.radius;
  const height = feel.height;
  const disposables = [];
  const keep = (thing) => {
    disposables.push(thing);
    return thing;
  };

  /*
   * The body, on the same bones as the player's, inside the same capsule.
   *
   * The capsule itself is not drawn any more and has not otherwise changed: it
   * is still what `npc.sees` measures against, still what the routes are
   * authored to fit, and still the only thing any distance in this project
   * knows about.
   */
  const rig = createRig({
    height,
    radius,
    colour: department.colour,
    // Everybody's shirt is the house colour, so a department reads off the body
    // and the crew do not turn into seven different species.
    accent: 0xe8e4dc,
    // Their own skin and hair, the same every time, off who they are.
    look: npc.data.id,
    // And the rest of how they look: build, hair, face, and what they wear.
    style: appearanceFor(npc.data, departmentsById[npc.data.department]?.colour ?? null),
  });
  group.add(rig.object3D);

  /**
   * How fast they are actually going, measured rather than asked for.
   *
   * An NPC has no speed getter — it has a route and a position that moves along
   * it — and adding one for the sake of a walk cycle would be putting an
   * animation flag into the simulation, which is the one thing this layer is
   * not allowed to do. The distance between where they were and where they are
   * is the same number and costs nothing.
   */
  const was = { x: npc.position.x, z: npc.position.z };

  let cone = null;
  let coneMaterial = null;
  if (npc.hasVision) {
    const { range, halfAngle } = npc.data.vision;
    const sweep = (halfAngle * 2 * Math.PI) / 180;
    /*
     * The arc is built centred on -Y in the circle's own plane, and laying the
     * circle down (a quarter turn about X) carries -Y to +Z: the way the body
     * faces, and the way `npc.sees()` looks, which is (sin yaw, cos yaw). There
     * used to be a half turn about Z as well, which put the cone out of every
     * back while perception looked forward; `rig.mjs` now measures the drawn
     * cone against the body's toes and against what the NPC actually sees.
     */
    const geometry = keep(
      new THREE.CircleGeometry(ft(range), 24, -sweep / 2 - Math.PI / 2, sweep)
    );
    coneMaterial = keep(
      new THREE.MeshBasicMaterial({
        color: 0xe0685f,
        transparent: true,
        opacity: 0.1,
        side: THREE.DoubleSide,
        depthWrite: false,
      })
    );
    cone = new THREE.Mesh(geometry, coneMaterial);
    cone.rotation.x = -Math.PI / 2;
    cone.position.y = 0.03;
    group.add(cone);
  }

  const label = document.createElement('div');
  label.className = 'nametag';
  label.innerHTML = `<b>${npc.data.name}</b><span>${department.name}</span><i></i>`;
  label.style.setProperty('--dept', `#${department.colour.toString(16).padStart(6, '0')}`);
  label.hidden = true;
  parent.append(label);
  /*
   * The third line, which is what they are doing about you.
   *
   * It carries two things and they are the same kind of thing: what somebody
   * about to act is about to do, and whether somebody you walked past thinks
   * anything of you. Both are properties of a person in a room and both belong
   * over that person rather than in a list somewhere — there is no relationship
   * panel in this game and there is not going to be one.
   *
   * It is words because this is a greybox. A tell performed physically — the
   * walk off toward the office — is perfectly legible as *movement* and
   * completely ambiguous as *intent* the first time you see it. The words are
   * what make the walk mean something once; after that the walk is enough.
   */
  const doing = label.querySelector('i');
  let said = null;

  const head = new THREE.Vector3();

  /** Seconds this person has stood still, and how the drawn body sits off the true one. */
  let stillFor = 0;
  let drawnTurn = 0;
  const drawnAt = { x: 0, y: 0, z: 0 };

  function nearestSeat() {
    let best = null;
    let bestDistance = ft(SIT_REACH);
    for (const seat of seats) {
      const d = Math.hypot(seat.x - npc.position.x, seat.z - npc.position.z);
      if (d < bestDistance) {
        bestDistance = d;
        best = seat;
      }
    }
    return best;
  }

  /**
   * How far a name tag carries.
   *
   * Uncapped, the service corridor stacks six overlapping tags into an
   * unreadable pile at the vanishing point, because a hundred and ninety feet
   * of straight line puts everybody on screen at once. Fifty feet is about the
   * distance at which you would actually recognise a colleague, and it fades
   * from thirty so nothing pops.
   */
  const TAG_FULL = ft(30);
  const TAG_GONE = ft(50);
  /*
   * And somebody the task on screen has nothing to do with, who is not doing
   * anything about you: only once you are beside them. A corridor of names
   * was one more thing on the screen to read that was not the job.
   */
  const QUIET_FULL = ft(9);
  const QUIET_GONE = ft(15);

  return {
    npc,
    object3D: group,
    label,
    /** The drawn body, for a check that wants to look at it. */
    rig,

    /** Pose, plus the name tag projected to where the head is on screen. */
    sync(camera, { width, height: viewHeight }, dt = 1 / 60) {
      // Off-shift staff are not standing somewhere else, they have gone home.
      group.visible = npc.present;
      if (!npc.present) {
        label.hidden = true;
        // Back on their feet for when they come on, so a person who left
        // mid-stride is not still mid-stride tomorrow morning.
        was.x = npc.position.x;
        was.z = npc.position.z;
        stillFor = 0;
        drawnTurn = 0;
        drawnAt.x = 0;
        drawnAt.y = 0;
        drawnAt.z = 0;
        rig.object3D.rotation.y = 0;
        rig.object3D.position.x = 0;
        rig.object3D.position.z = 0;
        rig.object3D.position.y = 0;
        rig.reset();
        return;
      }
      // Off the ground only when sitting in something.
      group.position.set(npc.position.x, npc.position.y ?? 0, npc.position.z);
      group.rotation.y = npc.yaw;

      /*
       * The walk, from the ground they covered since the last frame.
       *
       * A detour, a dwell at the end of a route and a person standing at a post
       * all come out of this correctly without any of them being a case: the
       * distance is the distance, and a body that did not move does not step.
       */
      const moved = Math.hypot(npc.position.x - was.x, npc.position.z - was.z);
      was.x = npc.position.x;
      was.z = npc.position.z;

      // --- the drawn body's own small life --------------------------------
      stillFor = moved < 0.002 ? stillFor + dt : 0;
      const seat = stillFor > SIT_AFTER && !npc.aboard ? nearestSeat() : null;
      const you = player();
      const speaking = Boolean(npc.note) || talkingTo() === npc;
      const living = life() ?? null;
      let wantYaw = 0;
      let wantAt = { x: 0, z: 0 };
      if (seat) {
        // Onto the bench, in the body's own frame, facing the table.
        const dx = seat.x - npc.position.x;
        const dz = seat.z - npc.position.z;
        const c = Math.cos(-npc.yaw);
        const sn = Math.sin(-npc.yaw);
        wantAt = { x: dx * c + dz * sn, z: -dx * sn + dz * c };
        wantYaw = wrapAngle(seat.yaw - npc.yaw);
      } else if (living?.facing && stillFor > 0.3) {
        // Turned toward whoever they are talking to, drawn only.
        const toward = Math.atan2(living.facing.x - npc.position.x, living.facing.z - npc.position.z);
        wantYaw = Math.max(-TURN_MOST, Math.min(TURN_MOST, wrapAngle(toward - npc.yaw)));
      } else if (speaking && you && Math.hypot(you.x - npc.position.x, you.z - npc.position.z) < ft(TURN_REACH)) {
        // A body faces (sin yaw, cos yaw), so toward you is atan2(dx, dz).
        const toward = Math.atan2(you.x - npc.position.x, you.z - npc.position.z);
        wantYaw = Math.max(-TURN_MOST, Math.min(TURN_MOST, wrapAngle(toward - npc.yaw)));
      }
      const ease = 1 - Math.exp(-dt * 4);
      drawnTurn += wrapAngle(wantYaw - drawnTurn) * ease;
      drawnAt.x += (wantAt.x - drawnAt.x) * ease;
      drawnAt.z += (wantAt.z - drawnAt.z) * ease;
      rig.object3D.rotation.y = drawnTurn;
      rig.object3D.position.x = drawnAt.x;
      rig.object3D.position.z = drawnAt.z;
      const sat = Boolean(seat) && Math.hypot(wantAt.x - drawnAt.x, wantAt.z - drawnAt.z) < 0.25;
      // Up onto the bench once there, and down off it when they stand.
      drawnAt.y += ((sat ? seat.y ?? 0 : 0) - drawnAt.y) * ease;
      rig.object3D.position.y = drawnAt.y;

      rig.pose(
        {
          speed: dt > 0 ? moved / dt : 0,
          walkSpeed: npc.data.speed || feel.walkSpeed,
          grounded: true,
          // A break's phone, sandwich or coffee, once they have stopped.
          carrying: living?.does && stillFor > SIT_AFTER ? 1 : 0,
          held: living?.does && stillFor > SIT_AFTER ? BREAK_HELD[living.does] ?? null : null,
          atTable: Boolean(living?.does),
          cart: null,
          // Somebody riding in the back of something is sitting in it.
          seated: Boolean(npc.aboard) || sat,
          climb: 'free',
          hidden: false,
          windup: 0,
          knocked: 0,
          talking: talkingTo() === npc,
        },
        dt
      );

      const note = npc.note;
      if (note !== said) {
        said = note;
        doing.textContent = note ?? '';
        doing.hidden = !note;
      }

      if (coneMaterial) {
        // The cone flushes as suspicion builds, which is the only warning the
        // player gets that they have been standing in it a beat too long.
        coneMaterial.opacity = 0.1 + npc.suspicion * 0.32;
      }

      const distance = Math.hypot(
        npc.position.x - camera.position.x,
        npc.position.z - camera.position.z
      );

      head.set(npc.position.x, height + 0.34, npc.position.z);
      head.project(camera);
      const behind = head.z > 1;
      const offscreen = Math.abs(head.x) > 1.1 || Math.abs(head.y) > 1.1;
      const loud = Boolean(note) || talkingTo() === npc || named();
      const full = loud ? TAG_FULL : QUIET_FULL;
      const gone = loud ? TAG_GONE : QUIET_GONE;
      if (behind || offscreen || distance > gone) {
        label.hidden = true;
        return;
      }
      label.hidden = false;
      label.style.opacity =
        distance <= full ? '1' : String(1 - (distance - full) / (gone - full));
      label.style.left = `${((head.x + 1) / 2) * width}px`;
      label.style.top = `${((1 - head.y) / 2) * viewHeight}px`;
    },

    /** What this body is playing. Read-only, for the verification scripts. */
    playing: () => rig.playing(),

    setLabelVisible(visible) {
      if (!visible) label.hidden = true;
    },

    dispose() {
      rig.dispose();
      for (const thing of disposables) thing.dispose?.();
      group.removeFromParent();
      label.remove();
    },
  };
}
